import axios, { type InternalAxiosRequestConfig } from 'axios'

export interface ApiError {
  code: string
  message: string
  details: Record<string, string>
}

interface RetryableConfig extends InternalAxiosRequestConfig {
  __retryCount?: number
  __authRetried?: boolean
}

// Auth hook — wired up by setupAuthInterceptors() in main.ts after Pinia is ready
type RefreshHandler = (failedConfig: RetryableConfig) => Promise<unknown>
let _authRefreshHandler: RefreshHandler | null = null

/**
 * Wire up 401-refresh behaviour. Call this once after Pinia is installed.
 * onRefresh should refresh the access token then retry the original request.
 */
export function setupAuthInterceptors(onRefresh: RefreshHandler): void {
  _authRefreshHandler = onRefresh
}

const USER_FRIENDLY_MESSAGES: Record<string, string> = {
  INTERNAL_ERROR: 'Something went wrong. Please try again.',
  SERVICE_UNAVAILABLE: 'Service is temporarily unavailable. Please try again shortly.',
  DATABASE_ERROR: 'A database error occurred. Please try again.',
  NETWORK_ERROR: 'Network error. Please check your connection and try again.',
}

const RETRY_STATUS_CODES = [503, 502, 504]
const MAX_RETRIES = 2

// A 401 from these endpoints means the credentials/refresh-token themselves were
// rejected — retrying via the refresh handler would just call back into /auth/refresh
// and loop forever, so these must fall straight through to normalizeError instead.
const AUTH_ENDPOINTS_EXCLUDED_FROM_REFRESH = ['/auth/login', '/auth/register', '/auth/refresh']

// A 401 only means "access token expired" if it actually came from this API — some
// infrastructure sitting in front of it (a proxy's Basic Auth wall, a WAF, a load
// balancer) can also return 401, but with an HTML/plain-text body instead of our
// JSON error shape. Treating those as "session expired" hides a real infra problem
// behind a misleading auth error, and pointlessly loops the refresh handler.
function hasApiErrorShape(data: unknown): data is { error: ApiError } {
  return (
    !!data &&
    typeof data === 'object' &&
    'error' in data &&
    !!(data as { error?: { code?: unknown } }).error?.code
  )
}

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000, // 10s default timeout
})

// Request interceptor: upload timeout + Bearer token injection
apiClient.interceptors.request.use((config) => {
  if (config.headers['Content-Type'] === 'multipart/form-data') {
    config.timeout = 30000 // 30s for uploads
  }
  const token = localStorage.getItem('accessToken')
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`
  }
  return config
})

// Response interceptor: 401 refresh + retry for transient failures + error normalization
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config as RetryableConfig | undefined

    // Handle 401: attempt silent token refresh then retry once
    const isExcludedFromRefresh = AUTH_ENDPOINTS_EXCLUDED_FROM_REFRESH.some((path) =>
      config?.url?.includes(path)
    )
    if (
      error.response?.status === 401 &&
      _authRefreshHandler &&
      config &&
      !config.__authRetried &&
      !isExcludedFromRefresh &&
      hasApiErrorShape(error.response?.data)
    ) {
      config.__authRetried = true
      return _authRefreshHandler(config)
    }

    const method = (config?.method || '').toUpperCase()

    // Only retry GET requests to avoid duplicate mutations
    const isGetRequest = method === 'GET' || method === ''
    const isNetworkError = !error.response
    const isRetryableStatus = error.response && RETRY_STATUS_CODES.includes(error.response.status)
    const retryCount = config?.__retryCount || 0

    if (
      config &&
      isGetRequest &&
      (isNetworkError || isRetryableStatus) &&
      retryCount < MAX_RETRIES
    ) {
      config.__retryCount = retryCount + 1
      const delay = Math.pow(2, config.__retryCount) * 500 // 1s, 2s
      await new Promise((r) => setTimeout(r, delay))
      return apiClient(config)
    }

    return Promise.reject(normalizeError(error))
  }
)

function normalizeError(error: unknown): ApiError {
  const axiosErr = error as {
    response?: { data?: unknown }
    message?: string
  }

  const data = axiosErr.response?.data
  if (hasApiErrorShape(data)) {
    const apiError = data.error
    return {
      ...apiError,
      message: USER_FRIENDLY_MESSAGES[apiError.code] || apiError.message,
    }
  }

  if (axiosErr.response) {
    // Got an HTTP response, but not in this API's error shape — something in
    // front of the app (proxy auth, WAF, load balancer) intercepted the request.
    return {
      code: 'UNEXPECTED_RESPONSE',
      message:
        'Unexpected response from the server — the request may have been blocked before reaching the app.',
      details: {},
    }
  }

  return {
    code: 'NETWORK_ERROR',
    message:
      USER_FRIENDLY_MESSAGES['NETWORK_ERROR'] || axiosErr.message || 'A network error occurred',
    details: {},
  }
}

export default apiClient
